/**
 * 交通費の確認・確定(2026-09-22 発注者指示。D-128)。
 *
 * 「月100時間以上で全日70%」は **管理者が確認して確定したときだけ** その月の日次集計を書き換える。
 *  - 確定の記録 = `commute_confirmations/{month}`(persons[personKey] に 誰が・いつ・何時間・金額の前後)
 *  - 確定・取り消しのたびに その月の日次集計の交通費を計算し直して保存(報酬の合計も同じだけ動く)
 *  - 監査ログに残す
 * 取込(CSV)や勤怠の手修正のあとの再計算(importRepo.settleCommute)も、この記録を見て70%を付ける。
 */
import { deleteField, doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { MonthStr } from '@core/lib/types';
import { fetchMonthSummaries } from '@core/dashboard/dashboardRepo';
import { commuteCandidates, settleCommuteForMonth, type CommuteCandidate } from './commuteSettle';

export type CommuteConfirmEntry = {
  name: string;
  confirmed_at: string;
  confirmed_by: string;
  confirmed_by_name: string;
  /** 確定したときの その月の実稼働(h) */
  hours: number;
  /** 確定前の交通費の合計 */
  before_total: number;
  /** 確定後(70%)の交通費の合計 */
  after_total: number;
};

export type CommuteConfirmation = { month: MonthStr; persons: Record<string, CommuteConfirmEntry> };

/** その月の確定の記録(無ければ空) */
export async function fetchCommuteConfirmation(month: MonthStr): Promise<CommuteConfirmation> {
  const snap = await getDoc(doc(getDb(), 'commute_confirmations', month));
  const persons = ((snap.data() ?? {}) as { persons?: Record<string, CommuteConfirmEntry> }).persons ?? {};
  return { month, persons };
}

/** 70%を確定した人(personKey) */
export async function fetchConfirmedKeys(month: MonthStr): Promise<Set<string>> {
  return new Set(Object.keys((await fetchCommuteConfirmation(month)).persons));
}

/** その月の「要確認」と確定済み(ダッシュボード・日報/サマリの通知と一覧に使う) */
export async function fetchCommuteCandidates(month: MonthStr): Promise<CommuteCandidate[]> {
  const [days, confirmed] = await Promise.all([fetchMonthSummaries(month), fetchConfirmedKeys(month)]);
  return commuteCandidates(
    days.map((d) => ({ id: d.id, staff: d.staff ?? [] })),
    confirmed,
  );
}

/** 確定の記録と日次集計の書き換えを、まとめて1回で保存する(途中で失敗したら何も残さない) */
async function writeWithResettle(month: MonthStr, confirmed: Set<string>, userId: string, record: (batch: ReturnType<typeof writeBatch>) => void): Promise<number> {
  const days = await fetchMonthSummaries(month);
  const changed = settleCommuteForMonth(
    days.map((d) => ({ id: d.id, staff: d.staff ?? [] })),
    confirmed,
  );
  const db = getDb();
  const batch = writeBatch(db);
  record(batch);
  for (const c of changed) batch.update(doc(db, 'daily_summary', c.id), { staff: c.staff, updated_by: userId, updated_at: serverTimestamp() });
  await batch.commit();
  return changed.length;
}

/** 70%で確定する。その月の全日の交通費を70%に書き換える。書き換えた日数を返す */
export async function confirmCommute(month: MonthStr, c: CommuteCandidate, user: { id: string; name: string }): Promise<number> {
  const conf = await fetchCommuteConfirmation(month);
  const keys = new Set([...Object.keys(conf.persons), c.key]);
  const entry: CommuteConfirmEntry = {
    name: c.name,
    confirmed_at: new Date().toISOString(),
    confirmed_by: user.id,
    confirmed_by_name: user.name,
    hours: c.hours,
    before_total: c.current,
    after_total: c.at70,
  };
  const n = await writeWithResettle(month, keys, user.id, (batch) =>
    batch.set(doc(getDb(), 'commute_confirmations', month), { month, persons: { [c.key]: entry }, updated_by: user.id, updated_at: serverTimestamp() }, { merge: true }),
  );
  await writeAuditLog({
    targetType: 'commute',
    targetId: `${month}_${c.key}`,
    action: 'confirm',
    by: user.id,
    changes: [`${month} ${c.name}: 交通費を70%で確定(実稼働 ${c.hours}h・${c.days}日 ／ ${c.current}円 → ${c.at70}円)`],
  });
  return n;
}

/** 確定を取り消す。その月の交通費を 0/30/50% に戻す。書き換えた日数を返す */
export async function unconfirmCommute(month: MonthStr, c: CommuteCandidate, user: { id: string; name: string }): Promise<number> {
  const conf = await fetchCommuteConfirmation(month);
  const keys = new Set(Object.keys(conf.persons).filter((k) => k !== c.key));
  const n = await writeWithResettle(month, keys, user.id, (batch) =>
    batch.set(doc(getDb(), 'commute_confirmations', month), { month, persons: { [c.key]: deleteField() }, updated_by: user.id, updated_at: serverTimestamp() }, { merge: true }),
  );
  await writeAuditLog({
    targetType: 'commute',
    targetId: `${month}_${c.key}`,
    action: 'unconfirm',
    by: user.id,
    changes: [`${month} ${c.name}: 交通費の70%確定を取り消し(0/30/50% に戻す)`],
  });
  return n;
}
